// CheckoutReceiptActions (Jose 2026-08-19) — the action row on the post-payment confirmation card:
// Email · Text · Print the receipt, plus Refund/Void. Rendered inside CelebrationProvider's checkout
// card so "email me that receipt" happens right there, not by hunting the Receipts tab.
//
// Wiring is the REAL services (correct architecture): emailService (queues → Firebase Trigger-Email),
// smsService.sendCheckoutReceipt (Twilio). Each is GATED — no email/phone on file, or SMS not configured,
// shows an honest message instead of failing silently. Refund/Void is manager-gated and reverses the sale
// out of reporting (status → cancelled). A real card money-back needs the createRefund Cloud Function
// (deferred to live Stripe).
import React, { useState } from 'react';
import { Mail, MessageSquare, Printer, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Receipt } from '../../shared/receipts';
import { printReceipt } from '../../utils/printReceipt';
import { emailService } from '../../services/emailService';
import { buildCheckoutReceiptBody } from '../../services/smsService';
import { communicationService } from '../../services/communicationService';
import { appointmentService } from '../../services/appointmentService';
import { refundSale } from '../../services/stripePaymentService';
import { usePermission } from '../../hooks/usePermission';

interface Props {
  receipt: Receipt;
  appointmentId: string;
  clientEmail?: string;
  clientPhone?: string;
  onClose: () => void;
}

const money = (n: number) => `$${(n || 0).toFixed(2)}`;

export const CheckoutReceiptActions: React.FC<Props> = ({ receipt, appointmentId, clientEmail, clientPhone, onClose }) => {
  // Gated on the 'refundVoid' permission (Settings → Team → Permissions), NOT a hardcoded role, so it's
  // tied to the permission system and configurable per member (Jose 2026-08-20). Owner always passes.
  const { hasPerm } = usePermission();
  const canRefund = hasPerm('refundVoid');
  // A Stripe-charged sale (payment link) can be refunded to the card; everything else voids locally.
  const isCardRefund = !!receipt.stripePaymentIntentId;
  const [busy, setBusy] = useState<string | null>(null);
  const [voidMode, setVoidMode] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const receiptText = () => {
    const rows = receipt.lines.map(l => `${l.qty > 1 ? l.qty + '× ' : ''}${l.label}  ${money(l.amount)}`);
    if (receipt.discount) rows.push(`Discount  -${money(receipt.discount)}`);
    if (receipt.emergencyFee) rows.push(`Emergency fee  ${money(receipt.emergencyFee)}`);
    if (receipt.tip) rows.push(`Tip  ${money(receipt.tip)}`);
    rows.push(`Total  ${money(receipt.total)}`);
    return `SLIK — receipt for ${receipt.clientName}\n${receipt.service}${receipt.barberName ? ' with ' + receipt.barberName : ''}\n\n${rows.join('\n')}\n\nPaid: ${receipt.paymentLabel}\nThank you!`;
  };

  const doPrint = () => { if (!printReceipt(receipt)) toast.error('Enable pop-ups to print'); };

  const doEmail = async () => {
    if (!clientEmail) { toast.error('No email on file for this client'); return; }
    setBusy('email');
    try {
      await emailService.sendEmail({
        to: [{ email: clientEmail, name: receipt.clientName }],
        subject: `Your receipt from SLIK — ${money(receipt.total)}`,
        body: receiptText(),
        reportType: 'custom',
      }, 'pos-checkout');
      toast.success(`📧 Receipt sent to ${clientEmail}`);
    } catch (e) {
      console.error('email receipt failed', e);
      toast.error('Could not send the email');
    } finally { setBusy(null); }
  };

  const doText = async () => {
    if (!clientPhone) { toast.error('No phone on file for this client'); return; }
    setBusy('text');
    try {
      // Route through SLIK Connect's durable queue (source 'checkout_receipt') → the receipt THREADS +
      // is labeled in the inbox and survives a WiFi blip, instead of the old direct Twilio fetch. (2026-10-30)
      const ok = await communicationService.sendToClient({
        clientName: receipt.clientName,
        phone: clientPhone,
        appointmentId,
        source: 'checkout_receipt',
        includeSaveContact: false, // a receipt isn't the moment for the "save our contact" nudge
        body: buildCheckoutReceiptBody({
          clientName: receipt.clientName,
          barberName: receipt.barberName,
          serviceName: receipt.service,
          originalPrice: receipt.serviceSubtotal + receipt.discount,
          discountAmount: receipt.discount,
          tipAmount: receipt.tip,
          totalCharged: receipt.total,
        }),
      });
      if (ok) toast.success(`💬 Receipt texted to ${clientPhone}`);
      else toast.error('Could not queue the text');
    } catch (e) {
      console.error('text receipt failed', e);
      toast.error('Could not send the text');
    } finally { setBusy(null); }
  };

  const doVoid = async () => {
    if (!canRefund) { toast.error("You don't have permission to refund/void a sale"); return; }
    setBusy('void');
    try {
      if (isCardRefund) {
        // Full refund: pass NO amount so Stripe refunds the exact CAPTURED PaymentIntent (incl. tax),
        // not a tax-free reconstructed total. BEHAVIOR-PIN: plausible-but-wrong audit (2026-09-10).
        const res = await refundSale(appointmentId);
        if (res.success) {
          toast.success(`↩︎ Refunded $${(res.amount ?? receipt.total).toFixed(2)} to the card`);
          onClose();
        } else {
          toast.error(res.error || 'Refund failed');
        }
      } else {
        await appointmentService.updateAppointment(appointmentId, {
          status: 'cancelled',
          voidReason: voidReason.trim() || 'Voided at checkout',
          voidedAt: new Date(),
        } as any);
        toast.success("↩︎ Sale voided — removed from today's totals");
        onClose();
      }
    } catch (e) {
      console.error('void/refund failed', e);
      toast.error('Could not complete the refund/void');
    } finally { setBusy(null); }
  };

  if (voidMode) {
    return (
      <div className="mt-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-left">
        <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">{isCardRefund ? `Refund $${receipt.total.toFixed(2)} to the card?` : 'Void this sale?'}</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">{isCardRefund ? "Sends the money back to the customer's card via Stripe and removes it from today's totals." : "Reverses it out of today's totals. (A card sale run on your terminal is refunded there.)"}</p>
        <input
          value={voidReason}
          onChange={e => setVoidReason(e.target.value)}
          placeholder="Reason (optional)"
          className="w-full mb-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
        />
        <div className="flex gap-2">
          <button onClick={() => setVoidMode(false)} className="flex-1 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold">Cancel</button>
          <button onClick={doVoid} disabled={busy === 'void'} className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">{isCardRefund ? 'Refund to card' : 'Void sale'}</button>
        </div>
      </div>
    );
  }

  const btn = 'flex flex-col items-center gap-1 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50 transition-colors';
  return (
    <div className="mt-4">
      <div className="grid grid-cols-3 gap-2">
        <button onClick={doEmail} disabled={busy === 'email'} className={btn}>
          <Mail className="w-5 h-5" /><span className="text-xs font-medium">Email</span>
        </button>
        <button onClick={doText} disabled={busy === 'text'} className={btn}>
          <MessageSquare className="w-5 h-5" /><span className="text-xs font-medium">Text</span>
        </button>
        <button onClick={doPrint} className={btn}>
          <Printer className="w-5 h-5" /><span className="text-xs font-medium">Print</span>
        </button>
      </div>
      <button
        onClick={() => (canRefund ? setVoidMode(true) : toast.error("You don't have permission to refund/void a sale"))}
        className="mt-2 w-full py-2 rounded-xl text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center gap-1.5 transition-colors"
      >
        <Undo2 className="w-4 h-4" /> Refund / Void
      </button>
    </div>
  );
};

export default CheckoutReceiptActions;
